import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  fetchAllSchemas,
  fetchSchemaBySlug,
  fetchSchemaHistory as fetchSchemaHistoryAPI,
  createSchema as createSchemaAPI,
  updateSchema as updateSchemaAPI,
  Schema,
} from '_/api/schemas/schemas.api';
import { notification } from 'antd';

export const SCHEMAS_QUERY_KEY = (orgSlug: string): string[] => ['schemas', orgSlug];
export const SCHEMA_QUERY_KEY = (schemaSlug: string): string[] => ['schema', schemaSlug];

export const useSchemas = (orgSlug?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SCHEMAS_QUERY_KEY(orgSlug || ''),
    queryFn: fetchAllSchemas,
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSchema = (schemaSlug?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SCHEMA_QUERY_KEY(schemaSlug || ''),
    queryFn: () => fetchSchemaBySlug(schemaSlug!),
    enabled: !!schemaSlug && schemaSlug !== '_new',
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateSchema = (orgSlug: string): UseMutationResult<any, Error, Partial<Schema>> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: (schema: Partial<Schema>) => createSchemaAPI(schema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCHEMAS_QUERY_KEY(orgSlug) });
      notification.success({
        message: 'Schema Created',
      });
      void history({ to: `/orgs/${orgSlug}/schemas` });
    },
    onError: (error: any) => {
      console.error('Error creating schema', error);
      notification.error({
        message: error.messages?.[0] || 'Error creating schema',
      });
      throw error;
    },
  });
};

export const useUpdateSchema = (
  orgSlug: string,
  schemaSlug?: string,
): UseMutationResult<any, Error, { schemaId: string; updates: Partial<Schema> }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ schemaId, updates }: { schemaId: string; updates: Partial<Schema> }) => updateSchemaAPI(schemaId, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCHEMAS_QUERY_KEY(orgSlug) });
      if (schemaSlug) {
        void queryClient.invalidateQueries({ queryKey: SCHEMA_QUERY_KEY(schemaSlug) });
      }
      notification.success({
        message: 'Schema Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating schema', error);
      notification.error({
        message: error.messages?.[0] || 'Error updating schema',
      });
      throw error;
    },
  });
};

export const SCHEMA_HISTORY_QUERY_KEY = (schemaId: string): string[] => ['schema', schemaId, 'history'];

export const useSchemaHistory = (schemaId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SCHEMA_HISTORY_QUERY_KEY(schemaId || ''),
    queryFn: () => fetchSchemaHistoryAPI(schemaId!, undefined),
    enabled: !!schemaId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
